import { Box, Button, CircularProgress, Tooltip, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import DatePicker from 'components/date-picker';
import Dialog from 'components/dialog';
import Divider from 'components/divider';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import NumberField from 'components/number-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { Info, RefreshCw } from 'lucide-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { putScenarioCageData } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-put';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ScenarioCageData } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { UpdateScenarioCageDataInput, UpdateScenarioCageDataItemInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-cage-data-input.type';
import { UpdateScenarioOverviewInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-overview-input.type';

interface CageDataFormItem extends UpdateScenarioCageDataItemInput {
  fishGroupName: string;
}

interface CageDataForm {
  cageData: CageDataFormItem[];
}

const DialogCageData: React.FC = () => {
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const params = useParams<R2ScenarioParams>();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const intl = useIntl();
  const theme = useTheme();
  const { control: overviewControl } = useFormContext<UpdateScenarioOverviewInput>();
  const overviewCageData = useWatch({ control: overviewControl, name: 'cageData' });
  const overviewEndDate = useWatch({ control: overviewControl, name: 'endDate' });

  const defaultValues: CageDataForm = useMemo(
    () => ({
      cageData: scenario?.cageData
        ? scenario.cageData
          .filter(
            (fish) => overviewCageData.find((overviewCage) => overviewCage.id === fish.id)?.isIncluded ?? fish.isIncluded
          )
          .map((fish) => ({
            fishGroupId: fish.fishGroupId,
            fishGroupName: fish.fishGroupName,
            isIncluded: fish.isIncluded,
            startDate: fish.startDate,
            fishCount: fish.fishCount,
            meanWeight: fish.meanWeight,
            isUserEdited: fish.isUserEdited,
          }))
        : [],
    }),
    [overviewCageData, scenario?.cageData]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty, errors },
  } = useForm<CageDataForm>({
    defaultValues: defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues);
    }
  }, [defaultValues, isDirty, reset]);

  const { mutate, isPending: isLoading } = usePost<ScenarioCageData[], UpdateScenarioCageDataInput>(
    (data) => putScenarioCageData(scenario!.id, scenario!.globalSiteId, data),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getR2Scenario', params.globalSiteId, params.scenarioId]
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      reset(defaultValues);
      dialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error },
      });
    }
  );

  const { fields } = useFieldArray({
    control,
    name: 'cageData',
  });

  const hasMeanWeightError = Array.isArray(errors.cageData) && errors.cageData.some((item) => !!item?.meanWeight);

  const handleClose = () => {
    reset(defaultValues);
    dialogRef.current?.setOpen(false);
  };

  const handleOpen = () => {
    setTimeout(() => {
      trigger('cageData');
    }, 0);
  };

  const onSubmit = (data: CageDataForm) => {
    if (!scenario) return;

    const editedCageData = new Map(data.cageData.map((cage) => [cage.fishGroupId, cage]));

    mutate({
      cageData: scenario.cageData.map((cage) => {
        const editedCage = editedCageData.get(cage.fishGroupId);
        const isIncluded = overviewCageData.find((overviewCage) => overviewCage.id === cage.id)?.isIncluded ?? cage.isIncluded;

        return {
          fishGroupId: cage.fishGroupId,
          isIncluded,
          startDate: editedCage?.startDate ?? cage.startDate,
          fishCount: editedCage?.fishCount ?? cage.fishCount,
          meanWeight: editedCage?.meanWeight ?? cage.meanWeight,
          isUserEdited: editedCage?.isUserEdited ?? cage.isUserEdited,
        };
      }),
    });
  };

  const options: HeadCell[] = [
    { id: 'name', label: 'Name', align: 'left' },
    { id: 'startDate', label: 'Start Date', align: 'left' },
    { id: 'fishCount', label: 'Fish Quantity', align: 'left' },
    { id: 'meanWeight', label: 'Mean Weight', align: 'left' },
  ];

  return (
    <Dialog
      title="Fish data"
      ref={dialogRef}
      maxWidth={'lg'}
      onClose={handleClose}
      button={<Button variant="outlined" onClick={handleOpen}>Edit Inputs</Button>}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={4}>
          <Divider dark flexItem />
          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>

            <Button
              variant="text"
              disabled={!isDirty}
              startIcon={<RefreshCw size={16} />}
              onClick={() => reset({ cageData: defaultValues.cageData })}>
              Reset data
            </Button>
          </Box>
          <List dark options={options}>
            {fields.map((field, index) => (
              <ListRow key={field.fishGroupId} sx={(theme) => ({ backgroundColor: `${theme.palette.lightBlue.o25} ` })}>
                {/* Name Field */}
                <ListCell>
                  <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1} justifyContent={'space-between'}>
                    <Box display={'flex'} flexDirection={'column'}>
                      <Typography>{field.fishGroupName}</Typography>
                      {field.isUserEdited && <Typography color="text.secondary">Edited after import</Typography>}
                    </Box>
                    {field.isUserEdited && (
                      <Tooltip title="This row has been edited by a user">
                        <Box>
                          <Info />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </ListCell>

                {/* From Date Field */}
                <ListCell>
                  <DatePicker
                    dark
                    fullWidth
                    hiddenLabel
                    controller={{
                      name: `cageData.${index}.startDate`,
                      control,
                      rules: {
                        required: 'Start Date is required',
                        validate: (value) =>
                          !value || !overviewEndDate || dayjs(value).isBefore(dayjs(overviewEndDate))
                            ? true
                            : 'Start date must be before the scenario end date',
                      },
                    }}
                    label="Start Date"
                  />
                </ListCell>

                {/* Fish Quantity Field */}
                <ListCell align="right">
                  <NumberField
                    fullWidth
                    dark
                    controller={{
                      name: `cageData.${index}.fishCount`,
                      control,
                      rules: {
                        required: 'Fish Quantity is required',
                        validate: (value) => (value > 0 ? true : 'Must be greater than 0'),
                      },
                    }}
                    hiddenLabel
                    label="Fish Quantity"
                  />
                </ListCell>

                {/* Average Weight Field */}
                <ListCell align="right">
                  <NumberField
                    fullWidth
                    dark
                    controller={{
                      name: `cageData.${index}.meanWeight`,
                      control,
                      rules: {
                        required: 'Average Weight is required',
                        validate: (value) => {
                          const num = Number(value);
                          if (isNaN(num) || num < 50 || num > 3000) return 'Weight must be between 50 and 3000 g';
                          return true;
                        },
                      },
                    }}
                    hiddenLabel
                    label="Average Weight"
                    endAdornment="g"
                  />
                </ListCell>
              </ListRow>
            ))}
          </List>
          <Divider dark flexItem />
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant={'outlined'} disabled={isLoading} onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} startIcon={isLoading ? <CircularProgress color="inherit" size={18} /> : null}>
              Update
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default React.memo(DialogCageData);
